import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import ContractPDF from './ContractPDF'
import { useLocation } from 'react-router-dom'
import Contract from './Contract'
import { getObject, getObjectsWithFilter } from '../../../api/apiClientCrud'
import { toast } from 'react-toastify'
import { useAuth } from '../../../context/AuthContext'
import Toolbar from '../../toolbar/Toolbar'
import { useOutletContext } from 'react-router-dom'
import SettingsIcon from '@mui/icons-material/Settings'
import EmailIcon from '@mui/icons-material/Email'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import PDFReader from '../../pdfReader/PDFReader'
import StatusField from '../../fields/StatusField'
import Loading from '../../common/Loading'
import NotFound from '../../common/NotFound'
import { pdf } from '@react-pdf/renderer'
import { sendDocumentEmail } from '../../../utils/emailUtils'
import { fileToBase64, updateAttachments, updateDocumentStatus } from '../../../utils/documentManager'
import DocumentPreview from '../../document/DocumentPreview'
import StatusDialog from '../../dialogs/StatusDialog'

const ViewContract = () => {
  const [contract, setContract] = useState({})
  const [company, setCompany] = useState({})
  const [img, setImg] = useState('')
  const [showPdf, setShowPdf] = useState(false)
  const [statusList, setStatusList] = useState([])
  const location = useLocation()
  const { user } = useAuth()
  const userRight = useOutletContext()
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false)
  const [isStatusChanged, setIsStatusChanged] = useState(false)
  const [files, setFiles] = useState([])
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [pdfUrl, setPdfUrl] = useState('')

  const sendEmail = useCallback(() => {
    try {
      sendDocumentEmail({
        type: 'contrat',
        object: contract,
        objectType: 'contract',
        user,
        company,
        customer: contract.customer,
        template: company.contract_email_template,
        component: (
          <ContractPDF contract_param={contract} company_param={company} logo_param={img} settings={settings} />
        ),
        setFiles,
        subjectPrefix: 'Votre contrat'
      })
    } catch (error) {
      toast.error('Erreur lors de l’envoi de l’email')
    }
  }, [contract, user, company, img, settings])

  const toolbarOptions = useMemo(
    () => ({
      objectType: 'contracts',
      title: () => {
        return `Contrat ${contract.contract_number}`
      },
      accessRight: userRight,
      actions: { edit: true, files: true, back: true, audits: true, list: true },
      customActionsRight: [
        {
          icon: () => <PictureAsPdfOutlinedIcon />,
          tooltip: 'Afficher PDF',
          onClick: () => setShowPdf((prev) => !prev)
        },
        {
          icon: () => <EmailIcon />,
          tooltip: 'Envoyer par mail',
          onClick: sendEmail,
          disabled: () => !contract.customer?.email
        },
        {
          icon: () => <SettingsIcon />,
          tooltip: 'Changer statut',
          onClick: () => openStatusDialog,
          disabled: () => contract.status >= 80
        }
      ],
      customComponentsLeft: [
        { component: () => <StatusField value={contract.status} type='contracts' statusList={statusList} /> }
      ]
    }),
    [userRight, contract.status, contract?.customer?.email, statusList, sendEmail]
  )

  const openStatusDialog = useCallback(() => {
    setIsStatusDialogOpen(true)
  }, [])

  const closeStatusDialog = useCallback(
    (newValue) => {
      setIsStatusDialogOpen(false)
      if (newValue !== undefined && newValue !== contract.status) {
        setContract((prevContract) => ({ ...prevContract, status: newValue }))
        setIsStatusChanged(true)
      }
    },
    [contract.status]
  )

  useEffect(() => {
    if (!isStatusChanged) return
    updateDocumentStatus('contracts', contract, user, toast, setIsStatusChanged)
  }, [isStatusChanged, contract, user])

  useEffect(() => {
    setContract((prev) => ({
      ...prev,
      attachments: updateAttachments(prev.attachments, files)
    }))
  }, [files])

  useEffect(() => {
    const contractId = location?.state?.id
    if (!contractId) {
      toast.error('Contrat introuvable.', { autoClose: 2000 })
      return
    }
    const fetchAll = async () => {
      try {
        const [statusRes, contractRes, companyRes, settingsRes] = await Promise.all([
          getObjectsWithFilter('status', { object: 'contracts' }),
          getObject('contracts', contractId),
          getObject('company', user.company_id),
          getObjectsWithFilter('settings', { company_id: user.company_id })
        ])

        setStatusList(statusRes.data)
        setContract(contractRes.data)
        setCompany(companyRes.data)
        setSettings(settingsRes.data)

        if (contractRes.data.attachments) {
          setFiles(contractRes.data.attachments)
          const attach = contractRes.data.attachments.find((f) => f.type === 'contract')
          if (attach) {
            const blob = new Blob([new Uint8Array(attach.file.data)], { type: 'application/pdf' })
            const base64 = await fileToBase64(blob)
            setPdfUrl(`data:application/pdf;base64,${base64}`)
          }
        }

        const logoAttach = companyRes.data.attachments?.find((a) => a.type === 'logo')
        if (logoAttach) {
          const blob = new Blob([new Uint8Array(logoAttach.file.data)])
          const base64 = await fileToBase64(blob)
          setImg(base64)
        }
      } catch (error) {
        toast.error('Erreur lors du chargement des données', { autoClose: 2000 })
      } finally {
        setLoading(false)
      }
    }

    fetchAll()
  }, [location, user.company_id])

  useEffect(() => {
    if (!contract?.id) return

    let url
    const generatePdfFromComponent = async () => {
      try {
        const attach = contract.attachments?.find((f) => f.type === 'contract')
        if (attach && attach.file?.data?.length) {
          const blob = new Blob([new Uint8Array(attach.file.data)], { type: 'application/pdf' })
          url = URL.createObjectURL(blob)
          setPdfUrl(url)
        } else {
          const component = (
            <ContractPDF contract_param={contract} company_param={company} logo_param={img} settings={settings} />
          )
          const doc = pdf(component)
          const blob = await doc.toBlob()
          url = URL.createObjectURL(blob)
          setPdfUrl(url)
        }
      } catch (err) {}
    }

    generatePdfFromComponent()
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [contract, company, img, settings])

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          height: '100%',
          minHeight: '100px',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading />
      </Box>
    )
  if (!contract?.id) return <NotFound title='Contrat introuvable' />

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 65px)', flexGrow: 1 }}>
      <Toolbar
        options={toolbarOptions}
        object={contract}
        setObject={setFiles}
        setParentObject={setContract}
        parentObject={contract}
      />
      {showPdf ? (
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <PDFReader url={pdfUrl} filename={`contrat_${contract?.contract_number}.pdf`} />
        </Box>
      ) : (
        <Box sx={{ overflowY: 'auto', flexGrow: 1, paddingTop: '15px', paddingBottom: '15px' }}>
          <DocumentPreview Component={Contract} document={contract} />
        </Box>
      )}

      <StatusDialog
        open={isStatusDialogOpen}
        value={contract.status}
        onClose={closeStatusDialog}
        type='contracts'
        statusList={statusList}
      />
    </Box>
  )
}

export default ViewContract
